import { Duration } from 'luxon';
import { getBrowserLanguage } from './get-browser-language';
import { getDisplayableDecimals } from './math';
export var NumberFormatTypes;
(function (NumberFormatTypes) {
    NumberFormatTypes["csNumber"] = "csNumber";
    NumberFormatTypes["csInteger"] = "csInteger";
    NumberFormatTypes["csRate"] = "csRate";
    NumberFormatTypes["csIntegerRate"] = "csIntegerRate";
    NumberFormatTypes["csSeconds"] = "csSeconds";
    NumberFormatTypes["csCurrency"] = "csCurrency";
    NumberFormatTypes["csPixels"] = "csPixels";
    NumberFormatTypes["csDuration"] = "csDuration";
    NumberFormatTypes["csVariation"] = "csVariation";
    NumberFormatTypes["csAbbreviation"] = "csAbbreviation";
})(NumberFormatTypes || (NumberFormatTypes = {}));
export class NumberFormat {
    static getFormater(locale, config) {
        const formaterKey = JSON.stringify(Object.assign(Object.assign({}, config), { locale }));
        let formater = NumberFormat.formaters.get(formaterKey);
        if (!formater) {
            formater = new Intl.NumberFormat(locale, config);
            NumberFormat.formaters.set(formaterKey, formater);
        }
        return formater;
    }
    static format(name) {
        const formatter = NumberFormat.formatters[name];
        if (formatter) {
            return formatter.bind(NumberFormat);
        }
        return NumberFormat.number.bind(NumberFormat);
    }
    static number(input, locale = getBrowserLanguage()) {
        if (input == null) {
            return '--';
        }
        const value = typeof input === 'string' ? parseFloat(input) : input;
        if (isNaN(value)) {
            return '--';
        }
        const digits = getDisplayableDecimals(value);
        return NumberFormat.getFormater(locale, {
            style: 'decimal',
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
        }).format(value);
    }
    static integer(input, locale = getBrowserLanguage()) {
        if (input == null) {
            return '--';
        }
        const value = typeof input === 'string' ? parseFloat(input) : input;
        if (isNaN(value)) {
            return '--';
        }
        return NumberFormat.getFormater(locale, {
            style: 'decimal',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    }
    static rate(input, locale = getBrowserLanguage(), options = {}) {
        if (input === null) {
            return 'N/A';
        }
        const value = typeof input === 'string' ? parseFloat(input) : input;
        if (isNaN(value)) {
            return '--';
        }
        const digits = getDisplayableDecimals(value);
        if (!options.isRawNumber) {
            return NumberFormat.getFormater(locale, {
                style: 'percent',
                minimumFractionDigits: digits,
                maximumFractionDigits: digits,
            }).format(value / 100);
        }
        else {
            return value.toString();
        }
    }
    static integerRate(input, locale = getBrowserLanguage()) {
        if (input == null) {
            return '--';
        }
        const value = typeof input === 'string' ? parseFloat(input) : input;
        if (isNaN(value)) {
            return '--';
        }
        return NumberFormat.getFormater(locale, { style: 'percent' }).format(value / 100);
    }
    static seconds(input, locale = getBrowserLanguage(), options = {}) {
        if (input == null) {
            return '--';
        }
        if (!options.isRawNumber) {
            return this.number(input, locale) + 's';
        }
        else {
            return this.number(input, locale);
        }
    }
    static currency(input, locale = getBrowserLanguage(), currency = 'EUR', options = {}) {
        if (input == null) {
            return '--';
        }
        const value = typeof input === 'string' ? parseFloat(input) : input;
        if (isNaN(value)) {
            return '--';
        }
        const digits = getDisplayableDecimals(value);
        if (!options.isRawNumber) {
            return NumberFormat.getFormater(locale, {
                style: 'currency',
                currency,
                minimumFractionDigits: digits,
                maximumFractionDigits: digits,
            }).format(parseFloat(value.toFixed(digits)));
        }
        else {
            return this.number(input, locale);
        }
    }
    static pixels(input, locale = getBrowserLanguage(), options = {}) {
        if (input == null) {
            return '--';
        }
        if (!options.isRawNumber) {
            return this.integer(input, locale) + 'px';
        }
        else {
            return this.integer(input, locale);
        }
    }
    static duration(seconds) {
        if (seconds == null) {
            return '--';
        }
        let signal = '';
        if (seconds < 0) {
            signal = '-';
            seconds = Math.abs(seconds);
        }
        const duration = Duration.fromObject({ seconds });
        if (Math.floor(Math.abs(duration.as('hours')))) {
            return signal + duration.toFormat("h 'h' m 'min' s 's'");
        }
        else if (Math.floor(Math.abs(duration.as('minutes')))) {
            return signal + duration.toFormat("m 'min' s 's'");
        }
        return signal + duration.toFormat("s 's'");
    }
    static variation(input, locale = getBrowserLanguage()) {
        if (input == null) {
            return '--';
        }
        const value = typeof input === 'string' ? parseFloat(input) : input;
        if (isNaN(value)) {
            return '--';
        }
        const rate = this.rate(Math.abs(value), locale);
        return value >= 0 ? `+ ${rate}` : `- ${rate}`;
    }
    static abbreviation(input, digits = 0) {
        const units = ['K', 'M'];
        for (let i = units.length - 1; i >= 0; i--) {
            const decimal = Math.pow(1000, i + 1);
            if (input <= -decimal || input >= decimal) {
                return +(input / decimal).toFixed(digits) + ' ' + units[i];
            }
        }
        return input.toFixed(digits);
    }
}
NumberFormat.formatters = {
    csNumber: NumberFormat.number,
    csInteger: NumberFormat.integer,
    csSeconds: NumberFormat.seconds,
    csRate: NumberFormat.rate,
    csIntegerRate: NumberFormat.integerRate,
    csPixels: NumberFormat.pixels,
    csDuration: NumberFormat.duration,
    csVariation: NumberFormat.variation,
    csCurrency: NumberFormat.currency,
    csAbbreviation: NumberFormat.abbreviation,
};
NumberFormat.formaters = new Map();
//# sourceMappingURL=number-format.js.map